/**
 * MentorSmartBanner
 *
 * Two-in-one dashboard widget for mentors:
 *  1. Next-session highlight — prominent card showing the next upcoming booking
 *     with a Join Now button and quick actions.
 *  2. Profile-completion checklist — smart, actionable checklist that detects
 *     exactly what the mentor is missing (bio, headline, skills, services,
 *     availability, social links) and links them to the right page.
 *
 * The checklist auto-dismisses when 100% complete and can be manually
 * collapsed/dismissed at any time (stored in localStorage per mentor).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MentorSmartBanner.css';

const IS_MOCK = process.env.REACT_APP_MOCK !== 'false';

/* ── helpers ──────────────────────────────────────────────── */

/** Read the mentor's saved session types from localStorage (set by SessionCatalog). */
function hasServices(slug) {
  try {
    const raw = localStorage.getItem(`db_session_types_${slug}`);
    if (!raw) return false;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0;
  } catch {
    return false;
  }
}

/** Check whether the mentor has saved their availability at least once. */
function hasAvailability(userId) {
  // In mock mode the availability panel is pre-filled from D.slots — count it done.
  if (IS_MOCK) return true;
  return !!localStorage.getItem(`db_mentor_avail_saved_${userId}`);
}

/**
 * Build the ordered checklist from the mentor's user object.
 * Each item has: key, icon, title, hint, done (bool), path (nav target).
 */
function buildChecks(user) {
  const slug = user?.mentorSlug || (user?.email || '').split('@')[0].toLowerCase();
  const skillsDone = Array.isArray(user?.skills)
    ? user.skills.length > 0
    : !!(user?.skills?.trim());

  return [
    {
      key: 'bio',
      icon: '📝',
      title: 'Write your bio',
      hint: 'Mentees read this before they book',
      done: !!(user?.bio && user.bio.length >= 30),
      path: '/dashboard/mentor/profile',
    },
    {
      key: 'headline',
      icon: '✍️',
      title: 'Add a headline',
      hint: 'Shown prominently on your public profile',
      done: !!user?.headline,
      path: '/dashboard/mentor/profile',
    },
    {
      key: 'skills',
      icon: '🛠️',
      title: 'List your skills',
      hint: 'Helps mentees discover you by technology',
      done: skillsDone,
      path: '/dashboard/mentor/profile',
    },
    {
      key: 'services',
      icon: '🎯',
      title: 'Create a session type',
      hint: 'Define what you offer and your pricing',
      done: hasServices(slug),
      path: '/dashboard/mentor/services',
    },
    {
      key: 'slots',
      icon: '📅',
      title: 'Set your availability',
      hint: 'Mentees can only book your open slots',
      done: hasAvailability(user?.id),
      path: '/dashboard/mentor/availability',
    },
    {
      key: 'social',
      icon: '🔗',
      title: 'Add social links',
      hint: 'LinkedIn / GitHub builds profile credibility',
      done: !!(user?.linkedinUrl || user?.githubUrl),
      path: '/dashboard/mentor/profile',
    },
  ];
}

function completionPct(checks) {
  const done = checks.filter(c => c.done).length;
  return Math.round((done / checks.length) * 100);
}

/** Format an ISO date string relative to today. */
function fmtDate(dateStr) {
  if (!dateStr) return '';
  if (dateStr === 'Today' || dateStr === 'Tomorrow') return dateStr;
  try {
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) return 'Today';
    if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
}

/* ── Next Session Card ────────────────────────────────────── */

function NextSessionCard({ session, onViewAll }) {
  const dateLabel = fmtDate(session.date);
  const isToday    = dateLabel === 'Today';
  const isTomorrow = dateLabel === 'Tomorrow';

  const badgeBg   = isToday ? '#16a34a' : isTomorrow ? '#2563eb' : '#6b7280';
  const badgeText = isToday ? '⚡ TODAY' : isTomorrow ? '📅 TOMORROW' : `📅 ${dateLabel}`;

  return (
    <div className="msb-next-session">
      {/* Header bar */}
      <div className="msb-ns-header">
        <span className="msb-ns-badge" style={{ background: badgeBg }}>{badgeText}</span>
        <span className="msb-ns-label">Next Session · {session.time}</span>
      </div>

      {/* Body */}
      <div className="msb-ns-body">
        {/* Avatar */}
        <div className="msb-ns-av" style={{ background: `#${session.menteeBg || '1DB954'}` }}>
          {session.menteeInit || session.mentee?.[0] || '?'}
        </div>

        {/* Info */}
        <div className="msb-ns-info">
          <div className="msb-ns-name">{session.mentee}</div>
          <div className="msb-ns-topic">📌 {session.topic}</div>
          <div className="msb-ns-meta">
            <span>{session.type}</span>
            <span className="msb-ns-dot">·</span>
            <span>{session.duration}</span>
            {session.status === 'confirmed' && (
              <>
                <span className="msb-ns-dot">·</span>
                <span className="msb-ns-confirmed">✓ Confirmed</span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="msb-ns-actions">
          {session.meetLink ? (
            <a
              href={session.meetLink}
              target="_blank"
              rel="noreferrer"
              className="msb-join-btn"
            >
              🎥 Join Now
            </a>
          ) : (
            <button className="msb-add-link-btn" onClick={onViewAll}>
              + Add Meet Link
            </button>
          )}
          <button className="msb-view-all-btn" onClick={onViewAll}>
            All Sessions →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Profile Checklist ────────────────────────────────────── */

function ProfileChecklist({ user }) {
  const nav = useNavigate();
  const dismissKey = `db_mentor_checklist_dismissed_${user?.id}`;

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey) === '1'
  );
  const [open, setOpen] = useState(true);

  const checks  = buildChecks(user);
  const score   = completionPct(checks);
  const pending = checks.filter(c => !c.done);
  const done    = checks.filter(c => c.done);
  const allDone = pending.length === 0;

  // Don't show if everything is done or manually dismissed
  if (allDone || dismissed) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div className="msb-checklist">
      {/* ── Collapsible header ── */}
      <div
        className="msb-cl-header"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
      >
        <div className="msb-cl-title-wrap">
          <span className="msb-cl-title">
            🚀 Complete your profile to start getting booked
          </span>
          <div className="msb-cl-progress-wrap">
            <div className="msb-cl-progress-bar" role="progressbar" aria-valuenow={score}>
              <div className="msb-cl-progress-fill" style={{ width: `${score}%` }} />
            </div>
            <span className="msb-cl-pct">{score}% done</span>
          </div>
        </div>

        <div className="msb-cl-hdr-right">
          <span className="msb-cl-count">
            {pending.length} step{pending.length !== 1 ? 's' : ''} left
          </span>
          <button
            className="msb-cl-icon-btn"
            aria-label={open ? 'Collapse' : 'Expand'}
            onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
          >
            {open ? '▲' : '▼'}
          </button>
          <button
            className="msb-cl-icon-btn msb-cl-dismiss-btn"
            aria-label="Dismiss checklist"
            onClick={handleDismiss}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Step cards grid ── */}
      {open && (
        <div className="msb-cl-steps">
          {/* Pending — actionable */}
          {pending.map(c => (
            <button
              key={c.key}
              className="msb-step msb-step-pending"
              onClick={() => nav(c.path)}
            >
              <span className="msb-step-icon">{c.icon}</span>
              <div className="msb-step-text">
                <span className="msb-step-title">{c.title}</span>
                <span className="msb-step-hint">{c.hint}</span>
              </div>
              <span className="msb-step-cta">Fix Now →</span>
            </button>
          ))}

          {/* Completed — faded */}
          {done.map(c => (
            <div key={c.key} className="msb-step msb-step-done">
              <span className="msb-step-icon">✅</span>
              <div className="msb-step-text">
                <span className="msb-step-title msb-step-done-lbl">{c.title}</span>
              </div>
              <span className="msb-step-done-badge">Done</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main export ──────────────────────────────────────────── */

/**
 * MentorSmartBanner
 *
 * Props:
 *   user     — mentor user object from useAuth()
 *   upcoming — array of upcoming session objects (already sorted by date)
 */
export default function MentorSmartBanner({ user, upcoming = [] }) {
  const nav  = useNavigate();
  const next = upcoming[0];

  const checks  = buildChecks(user);
  const score   = completionPct(checks);
  const allDone = score === 100;
  const dismissKey = `db_mentor_checklist_dismissed_${user?.id}`;
  const dismissed  = localStorage.getItem(dismissKey) === '1';

  // Nothing to show
  if (!next && (allDone || dismissed)) return null;

  return (
    <div className="msb-wrap">
      {next && (
        <NextSessionCard
          session={next}
          onViewAll={() => nav('/dashboard/mentor/sessions')}
        />
      )}
      <ProfileChecklist user={user} />
    </div>
  );
}
